import { useNavigate } from "react-router-dom";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import { useAdvertiser } from "../contexts/AdvertiserContext";
import { resolveVerticalDashboardConfig } from "../config/verticalDashboardConfig";
import "./CinemaAnalytics.css";
import "./GeneralIntelligence.css";

const INTELLIGENCE_PAGE_PROFILES = {
  general: {
    vertical: "general",
    dashboardConfig: resolveVerticalDashboardConfig("general"),
    title: "General Intelligence",
    description:
      "Campaign performance is organized around audience intent, platform response, market signals, conversion quality, and creative effectiveness. This view surfaces actionable insights for advertisers that do not require a vertical-specific intelligence model.",
    moduleLabel: "General Intelligence modules",
    modules: [
      {
        title: "Audience Intent",
        body:
          "Track signals that point to audience intent, including engagement, qualified traffic, search behavior, high-value response, and demand-building activity.",
      },
      {
        title: "Platform Response",
        body:
          "Organize channel performance around delivery, click and engagement response, reach, traffic quality, and the platforms creating the strongest measurable signal.",
      },
      {
        title: "Market Signals",
        body:
          "Read performance across audience, geography, timing, category, and competitive context so campaign decisions stay tied to current market response.",
      },
      {
        title: "Conversion Quality",
        body:
          "Use site, conversion, and downstream response signals to separate broad traffic from audiences showing stronger intent or action quality.",
      },
      {
        title: "Creative Performance",
        body:
          "Evaluate which messages, formats, offers, and placements are producing measurable audience response across the media mix.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate audience, platform, market, conversion, and creative signals into budget, targeting, and messaging decisions for the next optimization cycle.",
      },
    ],
  },
  travel_hospitality: {
    vertical: "travel_hospitality",
    dashboardConfig: resolveVerticalDashboardConfig("travel_hospitality"),
    title: "Travel Intelligence",
    description:
      "Campaign performance is organized around destination demand, trip-planning intent, booking behavior, feeder markets, seasonal travel windows, and conversion quality for hospitality and travel advertisers.",
    moduleLabel: "Travel Intelligence modules",
    modules: [
      {
        title: "Destination Demand",
        body:
          "Track signals that indicate interest in destinations, routes, stays, experiences, resorts, attractions, and travel packages.",
      },
      {
        title: "Booking Intent",
        body:
          "Identify audiences showing trip-planning, search, itinerary, booking, inquiry, reservation, or package-comparison behavior.",
      },
      {
        title: "Feeder Markets & Geos",
        body:
          "Read performance by origin market, destination market, drive/fly radius, seasonality, and high-value travel corridors.",
      },
      {
        title: "Traveler Segments",
        body:
          "Separate leisure, luxury, family, couples, business, event-driven, and adventure travelers where signal quality supports it.",
      },
      {
        title: "Creative & Offer Performance",
        body:
          "Evaluate which destination visuals, packages, seasonal offers, amenities, and experience messages drive qualified engagement.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate travel demand, audience, geo, creative, booking, and market signals into recommended budget, targeting, creative, and platform adjustments.",
      },
    ],
  },
  financial_services: {
    vertical: "financial_services",
    dashboardConfig: resolveVerticalDashboardConfig("financial_services"),
    title: "Finance Intelligence",
    description:
      "Campaign performance is organized around trust building, account and application intent, product consideration, qualified lead quality, and retention signals for financial services advertisers, with compliance-safe messaging throughout.",
    moduleLabel: "Finance Intelligence modules",
    modules: [
      {
        title: "Trust & Credibility",
        body:
          "Track engagement with educational content, credibility signals, and brand-trust touchpoints that precede consideration of financial products.",
      },
      {
        title: "Account & Application Intent",
        body:
          "Identify audiences showing account-opening, application-start, quote, pre-qualification, or consultation behavior.",
      },
      {
        title: "Product Consideration",
        body:
          "Read comparison, research, and education behavior to separate early interest from active product consideration.",
      },
      {
        title: "Qualified Lead Quality",
        body:
          "Use form completion, follow-through, and downstream response signals to separate broad traffic from qualified prospects.",
      },
      {
        title: "Retention & Relationship Signals",
        body:
          "Evaluate engagement from existing customers, cross-product interest, and long-term relationship signals where data exists.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate trust, intent, consideration, and lead-quality signals into budget, targeting, and messaging decisions that stay compliance-safe and avoid performance promises.",
      },
    ],
  },
  crypto_web3: {
    vertical: "crypto_web3",
    dashboardConfig: resolveVerticalDashboardConfig("crypto_web3"),
    title: "Crypto Intelligence",
    description:
      "Campaign performance is organized around product education, wallet and account intent, community growth, onboarding quality, and ecosystem participation for blockchain, crypto, and web3 advertisers — education-led and compliance-aware, never speculative.",
    moduleLabel: "Crypto Intelligence modules",
    modules: [
      {
        title: "Product Education",
        body:
          "Track engagement with explainers, documentation, tutorials, and education-led content that builds understanding before onboarding.",
      },
      {
        title: "Wallet & Account Intent",
        body:
          "Identify audiences showing wallet-creation, account-signup, or onboarding-start behavior across the acquisition funnel.",
      },
      {
        title: "Community Growth",
        body:
          "Read community joins, follows, developer activity, and participation signals across the ecosystem.",
      },
      {
        title: "Onboarding Quality",
        body:
          "Separate broad traffic from users who complete onboarding steps and show sustained product engagement.",
      },
      {
        title: "Trust & Compliance Signals",
        body:
          "Evaluate which messages build durable trust through product utility, security practices, and education rather than price speculation.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate education, intent, community, and onboarding signals into budget, targeting, and messaging decisions for the next optimization cycle.",
      },
    ],
  },
  health_wellness: {
    vertical: "health_wellness",
    dashboardConfig: resolveVerticalDashboardConfig("health_wellness"),
    title: "Wellness Intelligence",
    description:
      "Campaign performance is organized around wellness awareness, appointment and booking interest, membership intent, lifestyle engagement, and routine building for health and wellness advertisers, with responsible outcomes framing.",
    moduleLabel: "Wellness Intelligence modules",
    modules: [
      {
        title: "Wellness Awareness",
        body:
          "Track signals that indicate interest in wellness services, programs, studios, and lifestyle offerings.",
      },
      {
        title: "Booking & Appointment Interest",
        body:
          "Identify audiences showing appointment, booking, class-schedule, or consultation behavior.",
      },
      {
        title: "Membership Intent",
        body:
          "Read trial sign-ups, membership inquiries, and plan-comparison behavior to find audiences close to joining.",
      },
      {
        title: "Lifestyle Engagement",
        body:
          "Separate casual interest from audiences engaging with routines, programs, and community content.",
      },
      {
        title: "Routine Building",
        body:
          "Evaluate repeat visits, program completion, and habit-forming engagement where signal quality supports it.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate awareness, booking, membership, and engagement signals into budget, targeting, and messaging decisions with responsible, outcome-honest language.",
      },
    ],
  },
  healthcare_public_health: {
    vertical: "healthcare_public_health",
    dashboardConfig: resolveVerticalDashboardConfig("healthcare_public_health"),
    title: "Healthcare Intelligence",
    description:
      "Campaign performance is organized around patient education, appointment intent, service-line awareness, screening and prevention, access, and community trust for healthcare and public health advertisers, with compliance-sensitive messaging.",
    moduleLabel: "Healthcare Intelligence modules",
    modules: [
      {
        title: "Patient Education",
        body:
          "Track engagement with condition, service, and prevention education content across patient and caregiver audiences.",
      },
      {
        title: "Appointment Intent",
        body:
          "Identify audiences showing appointment-scheduling, find-a-provider, or contact behavior.",
      },
      {
        title: "Service-Line Awareness",
        body:
          "Read response by service line so investment follows demonstrated community need and interest.",
      },
      {
        title: "Screening & Prevention",
        body:
          "Evaluate response to screening, vaccination, and preventive-care messaging where programs exist.",
      },
      {
        title: "Access & Community Trust",
        body:
          "Separate broad reach from audiences showing access-seeking behavior, and track trust signals across communities served.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate education, appointment, service-line, and access signals into budget, targeting, and messaging decisions without aggressive sales language or unsupported clinical claims.",
      },
    ],
  },
  education: {
    vertical: "education",
    dashboardConfig: resolveVerticalDashboardConfig("education"),
    title: "Education Intelligence",
    description:
      "Campaign performance is organized around program awareness, enrollment interest, applications, information sessions, campus visits, and the admissions funnel across student and parent audiences.",
    moduleLabel: "Education Intelligence modules",
    modules: [
      {
        title: "Program Awareness",
        body:
          "Track signals that indicate interest in programs, degrees, certificates, and learning pathways.",
      },
      {
        title: "Enrollment Interest",
        body:
          "Identify audiences showing inquiry, request-for-information, or program-comparison behavior.",
      },
      {
        title: "Applications & Admissions Funnel",
        body:
          "Read application starts, completions, and admissions-stage progression to keep investment tied to funnel movement.",
      },
      {
        title: "Information Sessions & Campus Visits",
        body:
          "Evaluate registration and attendance interest for sessions, tours, open houses, and virtual events.",
      },
      {
        title: "Student & Parent Audiences",
        body:
          "Separate prospective student, parent, and adult-learner audiences where signal quality supports it.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate awareness, inquiry, application, and visit signals into budget, targeting, and messaging decisions for the next admissions cycle.",
      },
    ],
  },
  b2b_saas: {
    vertical: "b2b_saas",
    dashboardConfig: resolveVerticalDashboardConfig("b2b_saas"),
    title: "B2B Intelligence",
    description:
      "Campaign performance is organized around qualified lead generation, demo requests, account-based audiences, buyer committees, pipeline influence, and product education for B2B and SaaS advertisers.",
    moduleLabel: "B2B Intelligence modules",
    modules: [
      {
        title: "Qualified Leads",
        body:
          "Track lead quality using firmographic fit, engagement depth, and downstream sales-response signals.",
      },
      {
        title: "Demo & Trial Requests",
        body:
          "Identify audiences showing demo-request, trial-start, pricing, or sales-contact behavior.",
      },
      {
        title: "Account-Based Audiences",
        body:
          "Read engagement across target account lists and separate in-market accounts from broad reach.",
      },
      {
        title: "Buyer Committees",
        body:
          "Evaluate reach and response across the roles that influence the purchase decision, not just a single contact.",
      },
      {
        title: "Pipeline Influence",
        body:
          "Connect campaign response to pipeline creation, progression, and sales enablement where data exists.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate lead, demo, account, and pipeline signals into budget, targeting, and messaging decisions for the next optimization cycle.",
      },
    ],
  },
  nonprofit_public_sector: {
    vertical: "nonprofit_public_sector",
    dashboardConfig: resolveVerticalDashboardConfig("nonprofit_public_sector"),
    title: "Nonprofit Intelligence",
    description:
      "Campaign performance is organized around awareness, community impact, public service messaging, donation and volunteer intent where applicable, and stakeholder engagement for nonprofit and public sector advertisers.",
    moduleLabel: "Nonprofit Intelligence modules",
    modules: [
      {
        title: "Awareness & Reach",
        body:
          "Track how effectively campaigns reach the communities and stakeholders the organization serves.",
      },
      {
        title: "Community Impact",
        body:
          "Read engagement with mission, program, and impact content across the communities served.",
      },
      {
        title: "Donation & Volunteer Intent",
        body:
          "Identify audiences showing donation, volunteer-signup, or supporter behavior where applicable.",
      },
      {
        title: "Public Service Messaging",
        body:
          "Evaluate response to public service and civic information campaigns across audiences and geographies.",
      },
      {
        title: "Stakeholder Engagement & Trust",
        body:
          "Separate broad reach from engaged supporters, partners, and constituents, and track trust signals over time.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate awareness, impact, supporter, and engagement signals into budget, targeting, and messaging decisions for the next campaign cycle.",
      },
    ],
  },
  real_estate: {
    vertical: "real_estate",
    dashboardConfig: resolveVerticalDashboardConfig("real_estate"),
    title: "Real Estate Intelligence",
    description:
      "Campaign performance is organized around listing awareness, property inquiries, open-house intent, neighborhood interest, buyer and seller intent, and local market demand.",
    moduleLabel: "Real Estate Intelligence modules",
    modules: [
      {
        title: "Listing Awareness",
        body:
          "Track which listings, developments, and communities are drawing qualified attention.",
      },
      {
        title: "Property Inquiries",
        body:
          "Identify audiences showing inquiry, showing-request, or contact behavior on specific properties.",
      },
      {
        title: "Open-House Intent",
        body:
          "Read registration and attendance interest for open houses, tours, and viewing events.",
      },
      {
        title: "Neighborhood Interest",
        body:
          "Evaluate demand by neighborhood, school zone, and commute radius to find where interest concentrates.",
      },
      {
        title: "Buyer & Seller Intent",
        body:
          "Separate active buyers and prospective sellers from casual browsers where signal quality supports it.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate listing, inquiry, event, and local-demand signals into budget, targeting, and messaging decisions for the next listing cycle.",
      },
    ],
  },
  food_beverage: {
    vertical: "food_beverage",
    dashboardConfig: resolveVerticalDashboardConfig("food_beverage"),
    title: "Food & Beverage Intelligence",
    description:
      "Campaign performance is organized around local dining intent, reservations and orders, menu and offer awareness, delivery and takeout response, loyalty, and foot traffic for food and beverage advertisers.",
    moduleLabel: "Food & Beverage Intelligence modules",
    modules: [
      {
        title: "Local Dining Intent",
        body:
          "Track signals that indicate nearby dining interest across locations, dayparts, and occasions.",
      },
      {
        title: "Reservations & Orders",
        body:
          "Identify audiences showing reservation, online-order, or catering-inquiry behavior.",
      },
      {
        title: "Menu & Offer Awareness",
        body:
          "Read response to menu items, limited-time offers, and promotions to see what drives visits.",
      },
      {
        title: "Delivery & Takeout",
        body:
          "Evaluate delivery and takeout response separately from dine-in so channel investment follows actual ordering patterns.",
      },
      {
        title: "Loyalty & Repeat Visits",
        body:
          "Separate first-time interest from returning guests, loyalty engagement, and repeat-order signals.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate dining-intent, order, offer, and loyalty signals into budget, targeting, and messaging decisions that support covers, orders, and foot traffic.",
      },
    ],
  },
  cpg: {
    vertical: "cpg",
    dashboardConfig: resolveVerticalDashboardConfig("cpg"),
    title: "CPG Intelligence",
    description:
      "Campaign performance is organized around product discovery, category awareness, retail availability, trial, repeat purchase, basket lift, and shopper intent for consumer packaged goods advertisers.",
    moduleLabel: "CPG Intelligence modules",
    modules: [
      {
        title: "Product Discovery",
        body:
          "Track how effectively campaigns introduce products to new shoppers across the media mix.",
      },
      {
        title: "Category Awareness",
        body:
          "Read category-level interest and competitive context so the brand wins attention where shoppers decide.",
      },
      {
        title: "Retail Availability",
        body:
          "Evaluate response around where-to-buy, retailer pages, and store-locator engagement.",
      },
      {
        title: "Trial & Repeat Purchase",
        body:
          "Identify signals separating first-time trial from repeat-purchase and re-order behavior where data exists.",
      },
      {
        title: "Shopper Intent & Basket Lift",
        body:
          "Separate broad reach from shoppers showing purchase intent, and connect response to basket and sales lift where measurable.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate discovery, category, availability, and shopper-intent signals into budget, targeting, and messaging decisions for the next shopper cycle.",
      },
    ],
  },
  gaming: {
    vertical: "gaming",
    dashboardConfig: resolveVerticalDashboardConfig("gaming"),
    title: "Gaming Intelligence",
    description:
      "Campaign performance is organized around player acquisition, install intent, retention and re-engagement, engagement depth, community and esports response, and return on ad spend where monetization data exists.",
    moduleLabel: "Gaming Intelligence modules",
    modules: [
      {
        title: "Player Acquisition",
        body:
          "Track how effectively campaigns bring new players into the title across genres, platforms, and audience segments.",
      },
      {
        title: "Install & Sign-Up Intent",
        body:
          "Identify audiences showing store-page, wishlist, download, or account-creation behavior along the install path.",
      },
      {
        title: "Retention & Re-Engagement",
        body:
          "Read returning-player and lapsed-player response so investment supports retention and win-back where those signals exist.",
      },
      {
        title: "Engagement Depth",
        body:
          "Separate broad reach from players showing session, progression, and community-content engagement where data supports it.",
      },
      {
        title: "Community & Esports Response",
        body:
          "Evaluate creator content, community growth, and esports or event moments that concentrate attention around the title.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate acquisition, install, retention, engagement, and community signals into budget, targeting, and messaging decisions — weighting ROAS only where monetization data exists.",
      },
    ],
  },
  telecom: {
    vertical: "telecom",
    dashboardConfig: resolveVerticalDashboardConfig("telecom"),
    title: "Telecom Intelligence",
    description:
      "Campaign performance is organized around connectivity demand, plan and offer adoption, switching intent, coverage confidence, activation-path efficiency, and household and business connectivity for telecom advertisers.",
    moduleLabel: "Telecom Intelligence modules",
    modules: [
      {
        title: "Plan & Offer Demand",
        body:
          "Track signals that indicate interest in wireless, fiber, internet, and bundle offers across markets and segments.",
      },
      {
        title: "Switching Intent",
        body:
          "Identify audiences showing plan-comparison, provider-switch, or competitive-offer behavior where signal quality supports it.",
      },
      {
        title: "Coverage & Availability",
        body:
          "Read coverage-check, address-lookup, and store-locator engagement so investment follows serviceable demand.",
      },
      {
        title: "Activation Path",
        body:
          "Evaluate plan sign-up, line-activation, and upgrade behavior to keep spend tied to activation-path movement where data exists.",
      },
      {
        title: "Household & Business Connectivity",
        body:
          "Separate household decision makers from business connectivity buyers so messaging and budget follow the right segment.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate demand, switching, coverage, and activation signals into budget, targeting, and messaging decisions without promising network performance or coverage quality.",
      },
    ],
  },
  electronics: {
    vertical: "electronics",
    dashboardConfig: resolveVerticalDashboardConfig("electronics"),
    title: "Electronics Intelligence",
    description:
      "Campaign performance is organized around device consideration, launch-window response, feature and spec comparison, purchase intent, and retail and e-commerce consideration for consumer electronics advertisers.",
    moduleLabel: "Electronics Intelligence modules",
    modules: [
      {
        title: "Device Consideration",
        body:
          "Track which devices, lines, and categories are drawing qualified attention across the media mix.",
      },
      {
        title: "Launch Response",
        body:
          "Read launch-window, announcement, and pre-order response where those signals exist so spend follows launch momentum.",
      },
      {
        title: "Feature & Spec Comparison",
        body:
          "Identify audiences showing comparison, review, and spec-research behavior that precedes a purchase decision.",
      },
      {
        title: "Purchase Intent",
        body:
          "Separate broad interest from shoppers showing where-to-buy, product-page, and retailer engagement close to purchase.",
      },
      {
        title: "Retail & E-Commerce Consideration",
        body:
          "Evaluate response across retailer availability, e-commerce paths, and store-adjacent demand where data supports it.",
      },
      {
        title: "Recommended Optimizations",
        body:
          "Translate consideration, launch, comparison, and purchase-intent signals into budget, targeting, and messaging decisions for the next launch or upgrade cycle.",
      },
    ],
  },
};

export function resolveIntelligencePageProfile(vertical = "general") {
  const dashboardConfig = resolveVerticalDashboardConfig(vertical);
  return INTELLIGENCE_PAGE_PROFILES[dashboardConfig.vertical] || INTELLIGENCE_PAGE_PROFILES.general;
}

function GeneralIntelligence({ vertical = null }) {
  const navigate = useNavigate();
  const { selectedAdvertiserName, selectedAdvertiserVertical } = useAdvertiser() || {};
  // The shared /general-intelligence route resolves the selected advertiser's
  // vertical profile; explicit route props (e.g. /travel-intelligence) win.
  // Unknown or unset verticals keep the General Intelligence profile.
  const profile = resolveIntelligencePageProfile(
    vertical || selectedAdvertiserVertical || "general"
  );

  return (
    <div className="reports-dashboard general-intelligence-page">
      <div className="reports-content general-intelligence-content">
        <CphereHeaderLogo
          alt="Powered by Logo"
          className="general-analysis-poweredby-logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer" }}
        />

        <section className="general-intelligence-hero">
          <div className="general-intelligence-hero-copy">
            <div className="general-intelligence-kicker">
              {selectedAdvertiserName || "Selected Advertiser"}
            </div>
            <h1 className="general-intelligence-title">{profile.title}</h1>
            <p className="general-intelligence-copy">{profile.description}</p>
          </div>

          <div className="general-intelligence-hero-media" aria-hidden="true">
            <img
              src={profile.dashboardConfig.tileImage}
              alt=""
              className="general-intelligence-hero-image"
              draggable={false}
            />
          </div>
        </section>

        <section className="general-intelligence-grid" aria-label={profile.moduleLabel}>
          {profile.modules.map((module) => (
            <article className="general-intelligence-card" key={module.title}>
              <h2>{module.title}</h2>
              <p>{module.body}</p>
            </article>
          ))}
        </section>
      </div>
    </div>
  );
}

export default GeneralIntelligence;
